import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotelinfo } from 'frontend-utils/models';
import type { Day } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import { hasOTBDefaultValue } from 'lumen/utils/models/occupancy';

export default class RoomOccupancy extends Model {
  [Type] = 'room-occupancy' as const;

  @belongsTo('day', { async: false, inverse: 'roomOccupancies' })
  declare day: Day;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @attr('string') declare dateAggregation: string;
  @attr('hoteldate') declare extractDate: NonNullable<HoteldateTransformReturn>;
  @attr('string') declare message: string;
  @attr('string') declare roomId: string; // PMS room id
  @attr('number') declare roomsAvailable: number;
  @attr('number') declare totalCapacity: number;

  @service declare intl: Services['intl'];

  get availableRooms(): number | null {
    const { isMissing, roomsAvailable } = this;

    if (
      roomsAvailable == null ||
      isMissing ||
      hasOTBDefaultValue(roomsAvailable)
    ) {
      return null;
    }

    return roomsAvailable;
  }

  get formattedRoomsAvailable(): string | number {
    if (this.roomsAvailable === null || this.isMissing) {
      return this.intl.t('general.missing');
    } else if (this.isSoldOut) {
      return this.intl.t('general.soldout');
    }
    return this.roomsAvailable;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSoldOut() {
    return this.roomsAvailable === 0;
  }
}
